import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { SettingsNav } from './Branches';
import { IconDroplet } from '@tabler/icons-react';

export default function DervSettings() {
  const { orgId, userProfile } = useAuth();
  const [branchTanks, setBranchTanks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) {
        setLoading(false);
        return;
      }
      try {
        const [branchesSnap, orgSnap] = await Promise.all([
          getDocs(collection(db, 'organisations', resolvedOrgId, 'branches')),
          getDocs(collection(db, 'organisations', resolvedOrgId, 'users')),
        ]);

        const branches = branchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const enriched = await Promise.all(branches.map(async (branch) => {
          const tanksSnap = await getDocs(
            collection(db, 'organisations', resolvedOrgId, 'branches', branch.id, 'dervTanks')
          );
          const tanks = tanksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          return { ...branch, tanks };
        }));

        setBranchTanks(enriched.filter(b => b.tanks.length > 0));

        // Load org suppliers
        const { getDoc, doc } = await import('firebase/firestore');
        const orgDoc = await getDoc(doc(db, 'organisations', resolvedOrgId));
        if (orgDoc.exists()) {
          setSuppliers(orgDoc.data().dervSuppliers || []);
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [resolvedOrgId]);

  const getSupplier = (branchName) => {
    return suppliers.find(s => s.branch === branchName) ||
      suppliers.find(s => s.branch === 'All branches') ||
      suppliers[0] || null;
  };

  return (
    <Layout>
      <div style={{ display: 'flex', flex: 1, minHeight: '100vh' }}>
        <SettingsNav active="Derv settings" />
        <div style={{ flex: 1, padding: 'var(--main-padding)' }}>
          <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Derv tank settings</h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
            {loading ? 'Loading...' : `${branchTanks.reduce((sum, b) => sum + b.tanks.length, 0)} tank${branchTanks.reduce((sum, b) => sum + b.tanks.length, 0) !== 1 ? 's' : ''} configured`}
          </p>

          {loading ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', padding: '32px 0', textAlign: 'center' }}>Loading tanks...</div>
          ) : branchTanks.length === 0 ? (
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '48px 32px', textAlign: 'center' }}>
              <IconDroplet size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>No tanks configured</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Tanks are added by branch managers during onboarding</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {branchTanks.map(branch =>
                branch.tanks.map(tank => {
                  const supplier = getSupplier(branch.name);
                  return (
                    <div key={tank.id} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: 'var(--color-brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <IconDroplet size={16} color="var(--color-brand)" stroke={1.5} />
                          </div>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 400 }}>{branch.name} · {tank.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{branch.address || ''}</div>
                          </div>
                        </div>
                        <button style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>Edit</button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
                        {[
                          { label: 'Capacity', value: tank.capacityLitres ? `${tank.capacityLitres.toLocaleString()} L` : '—' },
                          { label: 'Order threshold', value: tank.orderThresholdPct ? `${tank.orderThresholdPct}%` : '—' },
                          { label: 'Supplier contact', value: supplier?.supplierName || '—' },
                          { label: 'Account number', value: supplier?.accountNumber || '—' },
                        ].map(r => (
                          <div key={r.label}>
                            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>{r.label}</div>
                            <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{r.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}